import {
  eventTitle as getEventTitle,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  formatDateParts,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import styles from './template-styles.module.css';
import { ICONS } from '@ui/icons/types';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import ButtonWidget from './widgets/button-widget';
import FooterWidget from './widgets/footer-widget';
import SocialWidget from './widgets/social-widget';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';

const DEFAULTS = templateStaticDefaults.montclair;

const MONTCLAIR_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.topBar]: { background: 'var(--accent)' },
  [BLOCK_ID.salutation]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '20px',
    fontWeight: 800,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    padding: '32px 56px',
  },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '40px',
    fontWeight: 600,
    lineHeight: '48px',
    textAlign: 'center',
    padding: '20px 56px',
  },
  [BLOCK_ID.mainImage]: { width: 488, height: 250, borderRadius: 'var(--image-radius)', padding: '0 56px 12px 56px' },
  [BLOCK_ID.description]: {
    color: 'var(--body)',
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    padding: '32px 56px',
  },
  [BLOCK_ID.dateTimeCard]: {
    accentColor: 'var(--secondary-accent)',
    headingColor: 'var(--heading)',
    borderColor: 'transparent',
    dividerColor: 'transparent',
    cardBackground: 'var(--secondary-bg)',
    timeRowPadding: '0 20px 20px 20px',
    padding: '12px 56px 0 56px',
  },
  [BLOCK_ID.cta]: {
    size: 'medium',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--body-font)',
    padding: '20px 56px',
  },
  [BLOCK_ID.divider]: { color: 'var(--secondary-bg)', padding: '0 56px' },
  [BLOCK_ID.social]: { color: 'var(--heading)', padding: '8px 56px' },
  [BLOCK_ID.legalText]: {
    color: '#889AB0',
    fontFamily: 'var(--body-font)',
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '18px',
    textAlign: 'center',
    padding: '20px 56px 40px 56px',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Montclair = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const secondaryBg = resolveThemedColor(
    customizations?.secondaryBackgroundColor,
    themeColors?.secondaryBackgroundColor,
    DEFAULTS.secondaryBackgroundColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '100px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '12px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    'Geist, Arial, sans-serif'
  );
  const bodyFont = resolveThemedFont(customizations?.bodyFont, theme?.font_pack?.body_name, 'Geist, Arial, sans-serif');
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription =
    description || customizations?.description || DEFAULTS.descriptionTemplate?.(getEventTitle(event, 'en'));
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = '#889AB0';
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      secondaryBg,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: MONTCLAIR_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Montclair"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--secondary-bg': secondaryBg,
            '--accent': accent,
            '--button-color': buttonColor,
            '--heading': heading,
            '--body': body,
            '--secondary-accent': secondaryAccent,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td style={{ background: 'var(--container)' }}>
                      {blocks.map((block) => (
                        <BlockShell
                          key={block.id}
                          block={block}
                          isEditMode={!!isEditMode}
                          isFirst={block.id === blocks[0]?.id}
                          isLast={block.id === blocks[blocks.length - 1]?.id}
                          selected={block.id === selectedBlockId}
                          onSelect={onSelectBlock}
                          onDelete={onDeleteBlock}
                          onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                          onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                        >
                          <BlockRenderer
                            block={block}
                            stylePreset={MONTCLAIR_BLOCK_PRESETS[block.id]}
                            onValueChange={onUpdateBlockValue}
                            onImageChange={onUpdateBlockImage}
                            context={blockContext}
                          />
                        </BlockShell>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Montclair"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--secondary-bg': secondaryBg,
          '--accent': accent,
          '--button-color': buttonColor,
          '--heading': heading,
          '--body': body,
          '--secondary-accent': secondaryAccent,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                <tr>
                  <td height={20} style={{ background: 'var(--accent)', fontSize: 0, lineHeight: '0' }}>
                    &nbsp;
                  </td>
                </tr>

                <tr>
                  <td style={{ background: 'var(--container)' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td>
                            <HeaderWidget
                              value={_salutation}
                              setValue={setSalutation}
                              color="var(--heading)"
                              fontFamily="var(--heading-font)"
                              fontSize="20px"
                              fontWeight={800}
                              textAlign="center"
                              whiteSpace="nowrap"
                              padding="32px 56px"
                            />
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <HeaderWidget
                              value={eventTitle}
                              setValue={setTitle}
                              color="var(--heading)"
                              fontFamily="var(--heading-font)"
                              fontSize="40px"
                              fontWeight={600}
                              lineHeight="48px"
                              textAlign="center"
                              padding="20px 56px"
                            />
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <ImageWidget
                              src={bodyImg}
                              setImage={setMainImage}
                              width={488}
                              height={250}
                              borderRadius={imageRadius}
                              padding="0 56px 12px 56px"
                            />
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <ParagraphWidget
                              value={eventDescription}
                              setValue={setDescription}
                              color="var(--body)"
                              fontFamily="var(--body-font)"
                              fontSize="16px"
                              fontWeight={400}
                              lineHeight="24px"
                              padding="32px 56px"
                            />
                          </td>
                        </tr>

                        {sessionTimestamp && (
                          <tr>
                            <td style={{ padding: '12px 56px 0 56px' }}>
                              <table
                                width="100%"
                                cellPadding={0}
                                cellSpacing={0}
                                border={0}
                                role="presentation"
                                style={{ background: 'var(--secondary-bg)', borderRadius: '12px' }}
                              >
                                <tbody>
                                  <tr>
                                    <td style={{ padding: '20px' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--secondary-accent)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 600,
                                              }}
                                            >
                                              Date
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--heading)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {sessionDateLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '0 20px 20px 20px' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--secondary-accent)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 600,
                                              }}
                                            >
                                              Time
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--heading)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {sessionTimeLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}

                        <tr>
                          <td>
                            <ButtonWidget
                              value={_cta}
                              setValue={setCta}
                              href={anchorHref}
                              size="medium"
                              color="var(--button-text)"
                              buttonColor="var(--button-color)"
                              fontFamily="var(--body-font)"
                              borderRadius={buttonRadius}
                              padding="20px 56px"
                            />
                          </td>
                        </tr>

                        <tr>
                          <td style={{ padding: '0 56px' }}>
                            <div style={{ borderTop: '1px solid var(--secondary-bg)', fontSize: 0, lineHeight: '0' }}>
                              &nbsp;
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <SocialWidget
                              links={[
                                { platform: ICONS.FACEBOOK, href: '#' },
                                { platform: ICONS.INSTAGRAM, href: '#' },
                                { platform: ICONS.TWITTER, href: '#' },
                              ]}
                              color="var(--heading)"
                              padding="8px 56px"
                            />
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <ParagraphWidget
                              value={DEFAULTS.complianceText}
                              color="#889AB0"
                              fontFamily="var(--body-font)"
                              fontSize="11px"
                              fontWeight={400}
                              lineHeight="18px"
                              textAlign="center"
                              padding="20px 56px 40px 56px"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
